/**
 * @file report-polling.service.ts
 * @description Root-provided service that owns the lifecycle of polling
 * for in-progress reports. Each tracked id is polled with an exponential
 * backoff, pauses while the tab is hidden, and emits a terminal event
 * (SUCCEEDED / FAILED / TIMEOUT) on `events$`. On success, a toast is
 * shown and a backend notification is created so the bell reflects the
 * event after a refresh.
 *
 * Each run has a single absolute polling budget (`TIMEOUT_MS`) anchored at
 * the run's start, not at page load. Boot reconciliation only resurrects
 * `PROCESSING` rows younger than that budget — see `#reconcileFromBackend`
 * for why stale rows must never be re-tracked.
 *
 * This file is built up across several commits — see the corresponding
 * plan in `docs/specs/2026-05-21-report-completion-polling-plan.md`.
 */
import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  NEVER,
  Observable,
  Subject,
  Subscription,
  catchError,
  fromEvent,
  of,
  switchMap,
  timer,
} from 'rxjs';
import type {
  CreateNotificationRequest,
  RegistryReportDto,
  ReportWithFilesDto,
} from '@kirigami/data-access/registry-api-contracts';
import { classifyAnalysisStatus } from '@kirigami/domain/analysis-rules';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { NotificationService } from './notification.service';
import { ReportService } from './report.service';
import { AnalyticsService } from './analytics.service';

export type TerminalStatus = 'SUCCEEDED' | 'FAILED' | 'TIMEOUT';

export interface ReportPollingEvent {
  reportId: string;
  status: TerminalStatus;
  response?: ReportWithFilesDto;
  emittedAt: number;
}

const BACKOFF_DELAYS_MS = [10_000, 20_000, 30_000, 60_000] as const;
const TIMEOUT_MS = 5 * 60_000;
const MAX_CONSECUTIVE_ERRORS = 3;

function extractStatus(response: ReportWithFilesDto): string {
  return (
    response.report?.status ||
    response.status ||
    response.report?.processing_status ||
    response.processing_status ||
    ''
  )
    .toString()
    .toUpperCase();
}

function delayFor(attemptIndex: number): number {
  const i = Math.min(attemptIndex, BACKOFF_DELAYS_MS.length - 1);
  return BACKOFF_DELAYS_MS[i];
}

/**
 * Best-effort start-of-run timestamp for a report row. `updated_at` is
 * fresher when the backend stamps it; `created_at` is always set at
 * creation. Returns null when neither parses — callers treat that as
 * "age unknown" and skip the row rather than risk polling a stale one.
 */
function rowTimestamp(row: RegistryReportDto): number | null {
  const raw = row.updated_at || row.created_at;
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

@Injectable({ providedIn: 'root' })
export class ReportPollingService {
  readonly #auth = inject(AuthService);
  readonly #toasts = inject(ToastService);
  readonly #notifications = inject(NotificationService);
  readonly #reports = inject(ReportService);
  readonly #analytics = inject(AnalyticsService);
  readonly #destroyRef = inject(DestroyRef);

  readonly #subs = new Map<string, Subscription>();
  readonly #events = new Subject<ReportPollingEvent>();
  readonly events$: Observable<ReportPollingEvent> = this.#events.asObservable();

  /**
   * Post-settlement wallet re-sync ladder. The engine callback flips the
   * report status and settles the wallet on separate timelines, so the first
   * `/users/get` after the poller sees a terminal status can capture
   * pre-settlement figures (QA 2026-07-28: meters stale until reload).
   * Same shape as PaddleService.PURCHASE_REFRESH_DELAYS_MS, with a
   * "figures moved" stop condition.
   */
  static readonly SETTLEMENT_REFRESH_DELAYS_MS = [0, 3000, 5000, 8000, 13000, 21000];

  /** Invalidates an in-flight ladder when a newer terminal event starts one. */
  #settlementRefreshGeneration = 0;

  /**
   * Reactive mirror of `#subs.size` (polled, in-flight runs). Kept in sync by
   * every mutation of `#subs` so `analysisActive` can react. Polled runs are
   * what survives a reload — see `#reconcileFromBackend`.
   */
  readonly #trackedCount = signal(0);

  /**
   * Count of manual "an analysis is starting" holds. Covers the windows a
   * poll cannot see yet: the upload pipeline before `track()`, and the
   * report-detail re-analysis flows that poll internally and never register
   * here. See `beginAnalysisHold`.
   */
  readonly #holdCount = signal(0);

  /**
   * Single global "an analysis is currently in progress" flag. True while any
   * run is polled OR any start-hold is open. Drives the upload-widget disable
   * and the start-analysis guards across the app so only one analysis runs at
   * a time. Falls back to false on every terminal outcome (SUCCEEDED / FAILED
   * / TIMEOUT untrack, or a released hold on error) so a failed run never
   * wedges the lock.
   */
  readonly analysisActive: Signal<boolean> = computed(
    () => this.#trackedCount() > 0 || this.#holdCount() > 0,
  );

  /** Visibility gate. Updated by the `visibilitychange` listener below. */
  readonly #visible$ = new BehaviorSubject<boolean>(this.#isVisible());

  constructor() {
    if (this.#isBrowser()) {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.#visible$.next(this.#isVisible());
        });
    }
    this.#reconcileFromBackend();
  }

  /**
   * Resurrects polling for runs that are plausibly still in flight after a
   * reload. Only `PROCESSING` rows qualify (`PENDING` means the analysis was
   * never started — there is no engine run to poll), and only when the row's
   * timestamp is younger than the polling budget: a run older than that
   * would have timed out already, so an old in-progress row is a stale row
   * (lost engine callback), not an in-flight run. Skipping stale rows here
   * is what prevents a recurring per-session TIMEOUT toast for rows the
   * backend will never flip to a terminal status. The timestamp is also
   * passed to `track` so the reconciled run's budget stays absolute per run
   * instead of restarting on every page load.
   */
  #reconcileFromBackend(): void {
    if (!this.#auth.user()) return;
    this.#reports
      .fetchReports()
      .pipe(catchError(() => of([] as RegistryReportDto[])))
      .subscribe((rows) => {
        const now = Date.now();
        for (const row of rows ?? []) {
          if (!row.id) continue;
          const status = (row.processing_status || row.status || '').toString().toUpperCase();
          if (status !== 'PROCESSING') continue;
          const startedAt = rowTimestamp(row);
          if (startedAt === null || now - startedAt >= TIMEOUT_MS) continue;
          this.track(row.id, { startedAt });
        }
      });
  }

  track(reportId: string, opts?: { startedAt?: number }): void {
    if (!this.#isBrowser()) return;
    if (!this.#auth.user()) return;
    if (this.#subs.has(reportId)) return;

    // Clamp to now so a skewed future timestamp can't extend the budget.
    const startedAt = Math.min(opts?.startedAt ?? Date.now(), Date.now());
    this.#subs.set(reportId, this.#pollOne(reportId, startedAt));
    this.#syncTrackedCount();
  }

  untrack(reportId: string): void {
    const sub = this.#subs.get(reportId);
    if (sub) {
      sub.unsubscribe();
      this.#subs.delete(reportId);
      this.#syncTrackedCount();
    }
  }

  stopAll(): void {
    for (const sub of this.#subs.values()) sub.unsubscribe();
    this.#subs.clear();
    this.#syncTrackedCount();
  }

  trackedIds(): ReadonlySet<string> {
    return new Set(this.#subs.keys());
  }

  /**
   * Acquires a "an analysis is starting" hold and returns a one-shot release
   * function. Callers use this to keep `analysisActive` true across a window
   * `track()` cannot cover yet — the upload pipeline before the report id is
   * known, or a report-detail re-analysis that polls internally.
   *
   * Contract:
   * - Call the returned function on BOTH success and failure of the run. On
   *   the upload happy path, call `track(reportId)` first, then release, so
   *   the flag never dips between hold and poll.
   * - The release is idempotent: calling it more than once is safe and only
   *   the first call decrements. This makes a "release on error after a
   *   partial success" path impossible to drive the count negative.
   */
  beginAnalysisHold(): () => void {
    this.#holdCount.update((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.#holdCount.update((n) => Math.max(0, n - 1));
    };
  }

  #syncTrackedCount(): void {
    this.#trackedCount.set(this.#subs.size);
  }

  #pollOne(reportId: string, startedAt: number): Subscription {
    let attempt = 0;
    let consecutiveErrors = 0;

    // Per-poller reschedule trigger. Each completed poll re-emits here to
    // arm the next tick for THIS report only. Pollers must never push into
    // the shared `#visible$` subject — that would reset every other
    // poller's pending timer.
    const reschedule$ = new BehaviorSubject<void>(undefined);

    return reschedule$
      .pipe(
        switchMap(() =>
          this.#visible$.pipe(switchMap((visible) => (visible ? timer(delayFor(attempt)) : NEVER))),
        ),
        switchMap(() =>
          this.#reports.fetchReportWithFiles(reportId).pipe(
            // Tag the response so we can distinguish error vs success
            // downstream while keeping the outer stream alive.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            catchError((err: any) => of({ __error: true, err } as const)),
          ),
        ),
      )
      .subscribe({
        next: (raw) => {
          attempt++;

          if ((raw as { __error?: boolean }).__error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const status = (raw as any).err?.status;
            if (status === 401 || status === 403) {
              // Auth gone — auth-error interceptor handles the redirect.
              this.untrack(reportId);
              return;
            }
            consecutiveErrors++;
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              console.warn(
                '[ReportPolling] stopping after consecutive errors',
                reportId,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (raw as any).err,
              );
              this.untrack(reportId);
              return;
            }
            reschedule$.next(undefined);
            return;
          }

          consecutiveErrors = 0;
          const response = raw as ReportWithFilesDto;
          const status = extractStatus(response);

          const outcome = classifyAnalysisStatus(status);
          if (outcome === 'succeeded') {
            this.#handleSuccess(reportId, response);
            return;
          }
          if (outcome === 'failed') {
            this.#handleFailure(reportId, response);
            return;
          }
          if (Date.now() - startedAt >= TIMEOUT_MS) {
            this.#handleTimeout(reportId);
            return;
          }
          reschedule$.next(undefined);
        },
      });
  }

  #handleSuccess(reportId: string, response: ReportWithFilesDto): void {
    const title = response.report?.title || 'Your analysis';
    this.#toasts.success('Analysis ready', {
      body: `${title} is ready to review.`,
      durationMs: 8000,
    });

    const senderEmail = this.#auth.user()?.email || '';
    const payload: CreateNotificationRequest = {
      id: reportId,
      sender: senderEmail,
      receiver: senderEmail,
      message: `Your analysis "${title}" is ready.`,
      type: 'ANALYSIS',
      status: 'UNREAD',
      extra: { report_id: reportId, report_title: title },
    };
    this.#notifications.createNotification(payload).subscribe({
      next: () => undefined,
      error: (err) => console.warn('[ReportPolling] createNotification failed', err),
    });

    this.#emitAndUntrack(reportId, 'SUCCEEDED', response);
  }

  #handleFailure(reportId: string, response: ReportWithFilesDto): void {
    const title = response.report?.title || 'Your analysis';
    this.#toasts.error('Analysis failed', {
      body: `${title} could not be processed.`,
      durationMs: 10_000,
    });
    this.#emitAndUntrack(reportId, 'FAILED', response);
  }

  #handleTimeout(reportId: string): void {
    this.#toasts.error('Analysis is taking longer than expected', {
      body: 'Please check back later.',
      durationMs: 10_000,
    });
    this.#emitAndUntrack(reportId, 'TIMEOUT', undefined);
  }

  #emitAndUntrack(
    reportId: string,
    status: TerminalStatus,
    response: ReportWithFilesDto | undefined,
  ): void {
    if (status === 'SUCCEEDED') {
      this.#analytics.track({ event: 'analysis_complete', report_id: reportId });
    } else {
      this.#analytics.track({
        event: 'analysis_fail',
        report_id: reportId,
        fail_reason: status === 'TIMEOUT' ? 'timeout' : 'failed',
      });
    }
    this.#events.next({ reportId, status, response, emittedAt: Date.now() });
    this.untrack(reportId);
    // Post-settlement wallet re-sync. SUCCEEDED debits real usage, FAILED
    // releases the reserve hold — both change the spendable balance every
    // credit meter renders from `AuthService.user()`. It lives HERE, not in
    // page components: this service is root-scoped and survives navigation.
    // TIMEOUT settles nothing server-side, so there is nothing to re-sync.
    if (status !== 'TIMEOUT') {
      this.#refreshCreditsUntilSettled();
    }
  }

  /**
   * Refreshes the profile on a bounded delay ladder until the active plan's
   * credit figures move (settlement landed) or the ladder is spent. A single
   * zero-delay refresh raced the backend settlement and lost often enough to
   * leave the meters stale until a manual reload (QA 2026-07-28); the ladder
   * is the same remedy PaddleService.refreshAfterPurchase applies to the
   * webhook lag. All refreshes are silent; a newer terminal event restarts
   * the ladder (generation guard) so baselines never mix.
   */
  #refreshCreditsUntilSettled(): void {
    const gen = ++this.#settlementRefreshGeneration;
    const baseline = this.#auth.activeUsage();
    const settled = (): boolean => {
      const now = this.#auth.activeUsage();
      return (
        now.creditsConsumed !== baseline.creditsConsumed ||
        now.creditsAvailable !== baseline.creditsAvailable ||
        now.creditsReserved !== baseline.creditsReserved
      );
    };
    const delays = ReportPollingService.SETTLEMENT_REFRESH_DELAYS_MS;
    const attempt = (i: number): void => {
      if (gen !== this.#settlementRefreshGeneration) return;
      const next = (): void => {
        if (gen !== this.#settlementRefreshGeneration) return;
        if (settled() || i + 1 >= delays.length) return;
        setTimeout(() => attempt(i + 1), delays[i + 1]);
      };
      this.#auth.loadUserProfile().subscribe({ next, error: next });
    };
    attempt(0);
  }

  #isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  #isVisible(): boolean {
    if (!this.#isBrowser()) return true; // SSR / tests without document
    if (typeof document.visibilityState === 'undefined') return true;
    return document.visibilityState !== 'hidden';
  }
}
